import React, { useCallback, useEffect } from 'react';
import { DropDownIcon } from '../../svgIcons';
import { NON_PLAYER_ROLE_OPTIONS } from '../../../utils/constants';
import { normalizeText } from '../../../utils/helpers';

/**
 * DropdownWithCheckBox Component
 *
 * @param {Function} onChange - Callback function called when selection changes
 * @param {Array} listOfOptions - Array of options to display (strings or objects)
 * @param {Array} defaultOptions - Array of initially selected options
 * @param {ReactNode} icon - Custom icon to display
 * @param {string} label - Placeholder text when no options selected
 * @param {boolean} allowMultiple - Whether to allow multiple selections (default: true). When false, dropdown closes after selection.
 * @param {number} maxSelection - Maximum number of selections allowed when allowMultiple is true (default: Infinity)
 * @param {boolean} showMaxSelectionMessage - Whether to show max selection reached message (default: true)
 * @param {string} valueKey - Property name to use as value when listOfOptions contains objects (default: 'value')
 * @param {string} labelKey - Property name to use as display label when listOfOptions contains objects (default: 'label')
 * @param {boolean} allowSearch - Whether to show search input for filtering options (default: false)
 * @param {boolean} extendedView - Whether to use extended view with RadioGroup and images (default: false)
 */
const DropdownWithCheckBox = ({
    onChange,
    listOfOptions = NON_PLAYER_ROLE_OPTIONS,
    defaultOptions = [],
    icon,
    label = 'Select options',
    allowMultiple = true,
    maxSelection = Infinity, // Default to unlimited selections
    showMaxSelectionMessage = true,
    valueKey = 'value',
    labelKey = 'label',
    allowSearch = true,
    extendedView = false
}) => {
    const [selectedOption, setSelectedOption] = React.useState(defaultOptions);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

    // Helper functions to handle both string arrays and object arrays
    const getItemValue = useCallback(
        item => {
            if (typeof item === 'string') {
                return item;
            }
            if (extendedView) {
                return item[valueKey] || item.id;
            }
            return item[valueKey];
        },
        [extendedView, valueKey]
    );

    const getItemLabel = item => {
        if (typeof item === 'string') {
            return item;
        }
        if (extendedView) {
            return item[labelKey] || item.name;
        }
        return item[labelKey];
    };

    const findItemByValue = value => {
        return listOfOptions.find(item => getItemValue(item) === value);
    };

    // Filter options based on search term
    const getFilteredOptions = () => {
        if (!allowSearch || !searchTerm.trim()) {
            return listOfOptions;
        }

        return listOfOptions.filter(item => {
            const itemLabel = getItemLabel(item);
            return normalizeText(itemLabel).includes(normalizeText(searchTerm));
        });
    };

    // Determine if max selection has been reached
    const isMaxSelectionReached =
        allowMultiple && selectedOption.length >= maxSelection;

    const handleChangeWithCallback = useCallback(
        item => {
            const itemValue = getItemValue(item);
            const isChecked = selectedOption.includes(itemValue);

            if (isChecked) {
                // Allow deselection
                setSelectedOption(
                    selectedOption.filter(value => value !== itemValue)
                );
            } else {
                if (allowMultiple) {
                    // Check if we've reached the maximum selection limit
                    if (selectedOption.length >= maxSelection) {
                        return; // Don't allow more selections
                    }
                    setSelectedOption([...selectedOption, itemValue]);
                } else {
                    // Single selection mode - replace current selection
                    setSelectedOption([itemValue]);
                    // Close dropdown after selection in single selection mode
                    setIsDropdownOpen(false);
                }
            }
        },
        [selectedOption, allowMultiple, maxSelection, getItemValue]
    );

    const getDisabledState = (item, selectedOptions) => {
        const itemValue = getItemValue(item);

        // Check if the item has a disabled property (for individual item disabling)
        if (typeof item === 'object' && item.disabled === true) {
            return true;
        }

        // If item is already selected, it should not be disabled (allow deselection)
        if (selectedOptions.includes(itemValue)) {
            return false;
        }

        if (allowMultiple) {
            // In multiple selection mode, disable if we've reached max selection
            return selectedOptions.length >= maxSelection;
        } else {
            // In single selection mode, allow selecting different options (they will replace the current selection)
            return false;
        }
    };

    useEffect(() => {
        onChange(selectedOption);
    }, [selectedOption]);

    // Auto-deselect disabled items when listOfOptions changes
    useEffect(() => {
        if (selectedOption.length > 0 && listOfOptions.length > 0) {
            const disabledValues = listOfOptions
                .filter(
                    item => typeof item === 'object' && item.disabled === true
                )
                .map(item => getItemValue(item));

            if (disabledValues.length > 0) {
                const hasDisabledSelected = selectedOption.some(value =>
                    disabledValues.includes(value)
                );

                if (hasDisabledSelected) {
                    const updatedSelection = selectedOption.filter(
                        value => !disabledValues.includes(value)
                    );
                    // Safe to call setState here as we're reacting to external prop changes (listOfOptions)
                    // and only updating when there's an actual change detected
                    setSelectedOption(updatedSelection);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listOfOptions]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            const dropdownElement = event.target.closest('.dropdown');
            if (!dropdownElement && isDropdownOpen) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div className='dropdown w-full'>
            <div
                tabIndex={0}
                role='button'
                className='m-1 w-full'
                onClick={e => {
                    e.stopPropagation();
                    setIsDropdownOpen(!isDropdownOpen);
                }}
            >
                {icon || (
                    <div className='flex items-center font-[400] font-poppins w-full'>
                        {selectedOption.length === 0 && (
                            <span className='text-gray-400'>{label}</span>
                        )}

                        <div className='flex scroll-smooth overflow-x-scroll flex-nowrap ml-2 flex-1 overflow-y-hidden'>
                            {selectedOption.map((value, idx) => {
                                const item = findItemByValue(value);
                                const displayLabel = item
                                    ? getItemLabel(item)
                                    : value;
                                return (
                                    <span
                                        className='pr-2 text-ellipsis whitespace-nowrap'
                                        key={value}
                                    >
                                        {displayLabel}{' '}
                                        {selectedOption.length - 1 !== idx
                                            ? ','
                                            : ''}
                                    </span>
                                );
                            })}
                        </div>

                        {/* Show selection counter if there's a max limit */}
                        {allowMultiple &&
                            maxSelection !== Infinity &&
                            selectedOption.length > 0 && (
                                <div className='text-xs text-gray-500 mr-2 flex-shrink-0'>
                                    {selectedOption.length}/{maxSelection}
                                </div>
                            )}
                        {/* {selectedOption.length <= 0 && (
                            <div className='absolute right-4 top-1/2 -translate-y-1/2 flex items-center'>
                                <DropDownIcon
                                    w={15}
                                    h={8}
                                    color={'black'}
                                    className={'flex-shrink-0'}
                                />
                            </div>
                        )} */}

                        <div className='ml-auto flex-shrink-0'>
                            <DropDownIcon
                                w={15}
                                h={8}
                                color={'black'}
                                className={'flex-shrink-0'}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ul
                tabIndex={0}
                className={`dropdown-content z-[1] shadow bg-white rounded-box max-h-[300px] overflow-y-auto ${
                    extendedView
                        ? 'max-w-[280px] min-w-[250px] px-0'
                        : 'max-w-[200px] px-2'
                } ${isDropdownOpen ? 'block' : 'hidden'}`}
                onClick={e => e.stopPropagation()}
            >
                {/* Search input if enabled */}
                {allowSearch && (
                    <li
                        className={`sticky top-0 bg-white z-10 py-2 my-2 border-b border-gray-200 ${
                            extendedView ? 'px-3' : 'px-0'
                        }`}
                    >
                        <input
                            type='text'
                            placeholder='Search options...'
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className='input bg-white input-sm w-full border border-gray-300 focus:border-blue-500 focus:outline-none'
                            onClick={e => e.stopPropagation()}
                        />
                    </li>
                )}

                {/* Show max selection message if enabled and limit reached */}
                {allowMultiple &&
                    showMaxSelectionMessage &&
                    isMaxSelectionReached && (
                        <li className='p-2 text-xs text-[#E67F0D] bg-[#FFEFE3] rounded mb-2 border border-[#E67F0D]'>
                            Maximum {maxSelection} selection
                            {maxSelection > 1 ? 's' : ''} reached
                        </li>
                    )}

                {extendedView // Multiple selection with extended view styling
                    ? getFilteredOptions().map((item, key) => {
                          const itemValue = getItemValue(item);
                          const itemLabel = getItemLabel(item);
                          const isDisabled = getDisabledState(
                              item,
                              selectedOption
                          );
                          const isSelected = selectedOption.includes(itemValue);

                          return (
                              <li
                                  onClick={() =>
                                      !isDisabled &&
                                      handleChangeWithCallback(item)
                                  }
                                  key={key}
                                  className={`min-h-[50px] my-1 flex items-center py-3 px-2 border-b border-gray-100 last:border-b-0 ${
                                      isDisabled
                                          ? 'opacity-50 cursor-not-allowed'
                                          : 'cursor-pointer hover:bg-gray-50'
                                  } ${isSelected ? 'bg-blue-50' : ''} rounded-none`}
                              >
                                  <div className='flex items-center justify-between w-full'>
                                      <div className='flex items-center'>
                                          {item.url && (
                                              <img
                                                  src={item.url}
                                                  alt={itemLabel}
                                                  className='w-8 h-6 rounded mr-3 object-cover flex-shrink-0'
                                              />
                                          )}
                                          <span className='text-sm font-medium text-gray-900'>
                                              {itemLabel}
                                          </span>
                                      </div>
                                      <div
                                          className={`w-6 h-6 border rounded-full flex items-center justify-center ${
                                              isSelected
                                                  ? 'bg-[#52FF00] border-black'
                                                  : 'bg-white border-gray-400'
                                          }`}
                                      >
                                          {isSelected && (
                                              <svg
                                                  className='w-4 h-4'
                                                  viewBox='0 0 17 12'
                                                  fill='none'
                                              >
                                                  <path
                                                      d='m25.576 11.414c0.56558 0.55188 0.56558 1.4439 0 1.9961l-9.404 9.176c-0.28213 0.27529-0.65247 0.41385-1.0228 0.41385-0.37034 0-0.74068-0.13855-1.0228-0.41385l-4.7019-4.588c-0.56584-0.55188-0.56584-1.4442 0-1.9961 0.56558-0.55214 1.4798-0.55214 2.0456 0l3.679 3.5899 8.3812-8.1779c0.56558-0.55214 1.4798-0.55214 2.0456 0z'
                                                      transform='translate(-9 -11)'
                                                      fill='black'
                                                  />
                                              </svg>
                                          )}
                                      </div>
                                  </div>
                              </li>
                          );
                      })
                    : // Default view with checkboxes
                      getFilteredOptions().map((item, key) => {
                          const itemValue = getItemValue(item);
                          const itemLabel = getItemLabel(item);
                          const isDisabled = getDisabledState(
                              item,
                              selectedOption
                          );
                          const isSelected = selectedOption.includes(itemValue);

                          return (
                              <li
                                  onClick={() =>
                                      !isDisabled &&
                                      handleChangeWithCallback(item)
                                  }
                                  key={key}
                                  className={`h-[30px] my-2 flex items-center ${
                                      isDisabled
                                          ? 'opacity-50 cursor-not-allowed'
                                          : 'cursor-pointer hover:bg-gray-100'
                                  } ${isSelected ? 'bg-blue-50' : ''} rounded px-1`}
                              >
                                  <input
                                      type='checkbox'
                                      checked={isSelected}
                                      className='checkbox checkbox-md checked-black-bg mr-2 cursor-pointer'
                                      disabled={isDisabled}
                                      readOnly
                                  />
                                  <label
                                      className={`cursor-pointer pr-0 ${
                                          isDisabled ? 'cursor-not-allowed' : ''
                                      }`}
                                  >
                                      <span className='text-[14px] pt-10'>
                                          {itemLabel}
                                      </span>
                                  </label>
                              </li>
                          );
                      })}

                {/* Show "No results found" message when search has no matches */}
                {allowSearch &&
                    searchTerm.trim() &&
                    getFilteredOptions().length === 0 && (
                        <li className='p-2 text-sm text-gray-500 text-center'>
                            No results found for "{searchTerm}"
                        </li>
                    )}
            </ul>
        </div>
    );
};

export default DropdownWithCheckBox;
